/**
 * One clock for the whole dashboard.
 *
 * Every report card holds its own query with its own `staleTime`, which is
 * right -- they are different questions over different periods. What was wrong
 * was that each one also decided *when* to go stale, so two cards reading the
 * same underlying rows could be minutes apart without saying so. An agent's
 * contribution to a figure looked different depending on which card you read
 * it from, and there was no way to tell whether that was a real difference or
 * simply an older answer.
 *
 * So: one staleness setting, shared, and one control that moves every card at
 * once. `refreshedAt` is what makes it honest -- a page of numbers with no
 * timestamp is a page that could be an hour old and looks exactly like one
 * that is current.
 *
 * ## Why this does not touch the layout
 *
 * The dashboard's key space is one prefix, which is what makes a single
 * invalidate possible, but it holds two different kinds of thing: figures, and
 * the arrangement they are drawn in. Layouts, templates and the filter lists
 * are configuration -- refetching them on a data refresh would, at best, do
 * nothing, and at worst stamp on an arrangement mid-save. `isFigures` draws
 * that line in one place rather than each caller remembering it.
 */

import { useCallback, useState } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";

/**
 * How long a report's figures stay fresh.
 *
 * A minute, which is what most cards already chose independently. Named so
 * the next card does not get to pick its own and drift.
 */
export const DASHBOARD_STALE_MS = 60_000;

/** Parts of the `dashboard` key space that are configuration, not figures. */
const NOT_FIGURES = new Set(["layout", "templates", "filters"]);

function isFigures(key: readonly unknown[]): boolean {
  if (key[0] !== "dashboard") return false;
  const part = key[1];
  if (typeof part !== "string") return false;
  if (NOT_FIGURES.has(part)) return false;
  // `["dashboard", "kpi", "metrics"]` is the catalogue of available metrics,
  // not a reading of any of them.
  return !(part === "kpi" && key[2] === "metrics");
}

export interface DashboardRefresh {
  /** When every card last agreed on the figures, or null before the first. */
  refreshedAt: Date | null;
  /** True while any card is in flight, so the control can say so. */
  busy: boolean;
  refresh: () => void;
}

export function useDashboardRefresh(): DashboardRefresh {
  const client = useQueryClient();
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const busy =
    useIsFetching({ predicate: (query) => isFigures(query.queryKey) }) > 0;

  const refresh = useCallback(() => {
    void client.invalidateQueries({
      predicate: (query) => isFigures(query.queryKey),
    });
    setRefreshedAt(new Date());
  }, [client]);

  return { refreshedAt, busy, refresh };
}
